import React, { useState, useEffect } from 'react';
import {
    collection,
    query,
    getDocs,
    where,
    onSnapshot
} from 'firebase/firestore';
import { db } from '../../../../Services/firebase-config';
import { useNavigate } from 'react-router-dom';
import {
    UserIcon,
    AcademicCapIcon,
    MailIcon,
    PhoneIcon,
    UserGroupIcon
} from '@heroicons/react/outline';

const MentorAssignment = () => {
    const [mentors, setMentors] = useState([]);
    const [teams, setTeams] = useState([]);
    const [allResources, setAllResources] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const navigate = useNavigate();

    useEffect(() => {
        let unsubscribeMentors;
        let unsubscribeTeams;
        let unsubscribeResources;

        // Fallback function for one-time fetch
        const fetchMentorsOnce = async () => {
            try {

                // Try different query variations
                const mentorsRef = collection(db, 'users');

                // First try exact match
                let mentorsQuery = query(mentorsRef, where('role', '==', 'Mentor'));
                let mentorsSnapshot = await getDocs(mentorsQuery);

                if (mentorsSnapshot.size === 0) {
                    // Try case insensitive search
                    mentorsQuery = query(mentorsRef, where('role', '==', 'mentor'));
                    mentorsSnapshot = await getDocs(mentorsQuery);
                    console.log('Case insensitive query result size:', mentorsSnapshot.size);
                }

                if (mentorsSnapshot.size === 0) {
                    // Get all users and filter manually
                    console.log('Getting all users and filtering manually...');
                    const allUsersSnapshot = await getDocs(mentorsRef);
                    console.log('All users count:', allUsersSnapshot.size);

                    const mentorsData = [];
                    allUsersSnapshot.forEach((doc) => {
                        const userData = doc.data();
                        console.log('User role:', userData.role, 'for user:', doc.id);
                        if (userData.role && (userData.role.toLowerCase() === 'mentor' || userData.role === 'Mentor')) {
                            mentorsData.push({
                                id: doc.id,
                                ...userData
                            });
                        }
                    });

                    console.log('Manually filtered mentors:', mentorsData);
                    setMentors(mentorsData);
                } else {
                    const mentorsData = [];
                    mentorsSnapshot.forEach((doc) => {
                        mentorsData.push({
                            id: doc.id,
                            ...doc.data()
                        });
                    });

                    console.log('Fetched mentors (fallback):', mentorsData);
                    setMentors(mentorsData);
                }
            } catch (error) {
                console.error('Error in fallback mentor fetch:', error);
            }
        };

        const fetchDataOnce = async () => {
            try {
                setLoading(true);
                await fetchMentorsOnce();

                // Fetch teams
                const teamsRef = collection(db, 'hackathonTeams');
                const teamsSnapshot = await getDocs(teamsRef);

                const teamsData = [];
                teamsSnapshot.forEach((doc) => {
                    teamsData.push({
                        id: doc.id,
                        ...doc.data()
                    });
                });

                setTeams(teamsData);

                // Fetch all resources
                const resourcesRef = collection(db, 'resources');
                const resourcesSnapshot = await getDocs(resourcesRef);

                const resourcesData = [];
                resourcesSnapshot.forEach((doc) => {
                    resourcesData.push({
                        id: doc.id,
                        ...doc.data()
                    });
                });

                setAllResources(resourcesData);
            } catch (error) {
                console.error('Error fetching data:', error);
            } finally {
                setLoading(false);
            }
        };

        const setupRealtimeListeners = async () => {
            try {
                setLoading(true);

                // Set up real-time listener for mentors
                const mentorsRef = collection(db, 'users');
                const mentorsQuery = query(mentorsRef, where('role', '==', 'Mentor'));

                unsubscribeMentors = onSnapshot(mentorsQuery, (snapshot) => {
                    console.log('Mentors snapshot received, size:', snapshot.size);
                    const mentorsData = [];
                    snapshot.forEach((doc) => {
                        const userData = doc.data();
                        console.log('Mentor document:', doc.id, userData);
                        mentorsData.push({
                            id: doc.id,
                            ...userData
                        });
                    });

                    console.log('Processed mentors data:', mentorsData);
                    setMentors(mentorsData);
                }, (error) => {
                    console.error('Error in mentors listener:', error);
                    // Fallback to one-time fetch if real-time fails
                    fetchMentorsOnce();
                });

                // Set up real-time listener for teams
                const teamsRef = collection(db, 'hackathonTeams');
                unsubscribeTeams = onSnapshot(teamsRef, (snapshot) => {
                    const teamsData = [];
                    snapshot.forEach((doc) => {
                        teamsData.push({
                            id: doc.id,
                            ...doc.data()
                        });
                    });
                    setTeams(teamsData);
                }, (error) => {
                    console.error('Error in teams listener:', error);
                });

                // Set up real-time listener for resources
                const resourcesRef = collection(db, 'resources');
                unsubscribeResources = onSnapshot(resourcesRef, (snapshot) => {
                    const resourcesData = [];
                    snapshot.forEach((doc) => {
                        resourcesData.push({
                            id: doc.id,
                            ...doc.data()
                        });
                    });
                    setAllResources(resourcesData);
                }, (error) => {
                    console.error('Error in resources listener:', error);
                });

            } catch (error) {
                console.error('Error setting up listeners:', error);
                // Fallback to one-time fetch
                fetchDataOnce();
            } finally {
                setLoading(false);
            }
        };

        setupRealtimeListeners();

        // Cleanup function
        return () => {
            if (unsubscribeMentors) {
                unsubscribeMentors();
            }
            if (unsubscribeTeams) {
                unsubscribeTeams();
            }
            if (unsubscribeResources) {
                unsubscribeResources();
            }
        };
    }, []);

    const getAssignedTeams = (mentorId) => {
        return teams.filter(team => team.mentorId === mentorId);
    };

    const getTeamResources = (team) => {
        if (!allResources || allResources.length === 0) return { mentorResources: 0, adminResources: 0 };

        // Filter mentor resources for this team (non-admin resources in allowedTeams)
        const mentorResources = allResources.filter(resource =>
            resource.mentorId === team.mentorId && (resource.visibility === 'public' || (resource.allowedTeams && resource.allowedTeams.includes(team.id)))
        );

        // Filter admin resources (admin resources are available to everyone)
        const adminResources = allResources.filter(resource =>
            resource.isAdminResource === true
        );

        return {
            mentorResources: mentorResources.length,
            adminResources: adminResources.length
        };
    };

    const filteredMentors = mentors.filter(mentor => {
        const searchLower = searchTerm.toLowerCase();
        const fullName = `${mentor.firstName || ''} ${mentor.lastName || ''}`.toLowerCase();
        return (
            fullName.includes(searchLower) ||
            mentor.email?.toLowerCase().includes(searchLower) ||
            mentor.displayName?.toLowerCase().includes(searchLower)
        );
    });

    if (loading) {
        return (
            <div className="flex justify-center items-center h-64">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-custom-blue-1"></div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-50 p-6">
            <div className="mb-6">
                <h1 className="text-2xl font-bold text-gray-900 mb-2">Mentor Assignment Overview</h1>
                <p className="text-gray-600">View mentor assignments and team distributions</p>
            </div>

            {/* Statistics */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-6">
                <div className="bg-white rounded-lg p-6 shadow-sm border border-gray-200">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-sm font-medium text-gray-600">Total Mentors</p>
                            <p className="text-3xl font-bold text-custom-blue-1 mt-2">{mentors.length}</p>
                        </div>
                        <div className="p-3 rounded-lg bg-blue-50">
                            <AcademicCapIcon className="h-8 w-8 text-custom-blue-1" />
                        </div>
                    </div>
                </div>
                <div className="bg-white rounded-lg p-6 shadow-sm border border-gray-200">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-sm font-medium text-gray-600">Assigned Mentors</p>
                            <p className="text-3xl font-bold text-custom-blue-1 mt-2">
                                {mentors.filter(mentor => getAssignedTeams(mentor.id).length > 0).length}
                            </p>
                        </div>
                        <div className="p-3 rounded-lg bg-green-50">
                            <UserGroupIcon className="h-8 w-8 text-green-600" />
                        </div>
                    </div>
                </div>
                <div className="bg-white rounded-lg p-6 shadow-sm border border-gray-200">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-sm font-medium text-gray-600">Available Mentors</p>
                            <p className="text-3xl font-bold text-custom-blue-1 mt-2">
                                {mentors.filter(mentor => getAssignedTeams(mentor.id).length === 0).length}
                            </p>
                        </div>
                        <div className="p-3 rounded-lg bg-orange-50">
                            <UserIcon className="h-8 w-8 text-orange-600" />
                        </div>
                    </div>
                </div>
                <div className="bg-white rounded-lg p-6 shadow-sm border border-gray-200">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-sm font-medium text-gray-600">Teams with Mentors</p>
                            <p className="text-3xl font-bold text-custom-blue-1 mt-2">
                                {teams.filter(team => team.mentor).length}
                            </p>
                        </div>
                        <div className="p-3 rounded-lg bg-purple-50">
                            <UserGroupIcon className="h-8 w-8 text-purple-600" />
                        </div>
                    </div>
                </div>
            </div>

            {/* Search */}
            <div className="mb-6">
                <div className="relative max-w-md">
                    <input
                        type="text"
                        placeholder="Search mentors..."
                        className="block w-full pl-3 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-custom-blue-1 focus:border-custom-blue-1"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                    />
                </div>
            </div>

            {/* Mentors Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {filteredMentors.map((mentor) => {
                    const assignedTeams = getAssignedTeams(mentor.id);
                    return (
                        <div key={mentor.id} className="bg-white rounded-lg shadow-md p-6">
                            <div className="flex items-center mb-4">
                                <div className="h-12 w-12 rounded-full bg-gray-300 flex items-center justify-center">
                                    {mentor.photoURL ? (
                                        <img
                                            src={mentor.photoURL}
                                            alt={mentor.firstName || mentor.displayName}
                                            className="h-12 w-12 rounded-full"
                                        />
                                    ) : (
                                        <UserIcon className="h-8 w-8 text-gray-600" />
                                    )}
                                </div>
                                <div className="ml-3 flex-1">
                                    <h3 className="text-lg font-medium text-gray-900">
                                        {mentor.firstName && mentor.lastName
                                            ? `${mentor.firstName} ${mentor.lastName}`
                                            : mentor.displayName || 'N/A'}
                                    </h3>
                                    <p className="text-sm text-gray-500 flex items-center">
                                        <AcademicCapIcon className="h-4 w-4 mr-1" />
                                        Mentor
                                    </p>
                                </div>
                            </div>

                            {/* Contact Information */}
                            <div className="space-y-2 mb-4">
                                <div className="flex items-center text-sm text-gray-600">
                                    <MailIcon className="h-4 w-4 mr-2" />
                                    <span className="truncate">{mentor.email}</span>
                                </div>
                                {mentor.phoneNumber && (
                                    <div className="flex items-center text-sm text-gray-600">
                                        <PhoneIcon className="h-4 w-4 mr-2" />
                                        <span>{mentor.phoneNumber}</span>
                                    </div>
                                )}
                            </div>

                            {/* Skills */}
                            {mentor.skills && mentor.skills.length > 0 && (
                                <div className="mb-4">
                                    <h4 className="text-sm font-medium text-gray-700 mb-2">Skills</h4>
                                    <div className="flex flex-wrap gap-1">
                                        {mentor.skills.slice(0, 3).map((skill, index) => (
                                            <span
                                                key={index}
                                                className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800"
                                            >
                                                {skill}
                                            </span>
                                        ))}
                                        {mentor.skills.length > 3 && (
                                            <span className="text-xs text-gray-500">
                                                +{mentor.skills.length - 3} more
                                            </span>
                                        )}
                                    </div>
                                </div>
                            )}

                            {/* Assignment Status */}
                            <div className="border-t pt-4">
                                <div className="flex items-center justify-between mb-2">
                                    <h4 className="text-sm font-medium text-gray-700">Assignment Status</h4>
                                    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${assignedTeams.length > 0
                                        ? 'bg-green-100 text-green-800'
                                        : 'bg-gray-100 text-gray-800'
                                        }`}>
                                        {assignedTeams.length > 0 ? 'Assigned' : 'Available'}
                                    </span>
                                </div>

                                {assignedTeams.length > 0 ? (
                                    <div className="space-y-2">
                                        {assignedTeams.map((team) => {
                                            const { mentorResources, adminResources } = getTeamResources(team);

                                            return (
                                                <div key={team.id} className="flex items-center justify-between p-2 bg-gray-50 rounded-md">
                                                    <div className="flex-1">
                                                        <p className="text-sm font-medium text-gray-900">{team.name}</p>
                                                        <p className="text-xs text-gray-500">
                                                            {team.members?.length || 0} members · {mentorResources} resources{adminResources > 0 ? ` · Admin: ${adminResources}` : ''}
                                                        </p>
                                                    </div>
                                                    <button
                                                        onClick={() => navigate(`/eventdashboard/team-details/${team.id}`)}
                                                        className="px-3 py-1 text-xs font-medium text-blue-600 bg-blue-100 rounded-md hover:bg-blue-200 focus:outline-none focus:ring-2 focus:ring-blue-500"
                                                    >
                                                        View Details
                                                    </button>
                                                </div>
                                            );
                                        })}
                                    </div>
                                ) : (
                                    <p className="text-sm text-gray-500">No teams assigned</p>
                                )}
                            </div>
                        </div>
                    );
                })}
            </div>

            {filteredMentors.length === 0 && (
                <div className="text-center py-12">
                    <AcademicCapIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                    <h3 className="text-lg font-medium text-gray-900 mb-2">No Mentors Found</h3>
                    <p className="text-gray-500">
                        {searchTerm ? 'Try adjusting your search criteria.' : 'No mentors are available in the system.'}
                    </p>
                </div>
            )}
        </div>
    );
};

export default MentorAssignment;
